"use client"

import { useEffect, useState } from "react";
import "./LegalAspects.css";

// Normaliza una sección legal a { title, content, items: [{ key, value }], note }.
// Tolera claves en inglés (title/content/items/key/value/note) y en español
// (titulo/contenido/items/clave/valor/nota_adicional).
const normalizeSection = (s) => {
    if (!s || typeof s !== "object") return null;
    const rawItems = Array.isArray(s.items) ? s.items : [];
    return {
        title: s.title ?? s.titulo ?? "",
        content: s.content ?? s.contenido ?? "",
        items: rawItems.map((it) => ({
            key: it?.key ?? it?.clave ?? "",
            value: it?.value ?? it?.valor ?? "",
        })),
        note: s.note ?? s.nota_adicional ?? "",
    };
};

// Convierte el bloque "contacto" (email/telefono/direccion) en una sección renderizable.
const contactToSection = (contacto) => {
    if (!contacto || typeof contacto !== "object") return null;
    const items = [
        contacto.email && { key: "Email", value: contacto.email },
        contacto.telefono && { key: "Teléfono", value: contacto.telefono },
        contacto.direccion && { key: "Dirección", value: contacto.direccion },
    ].filter(Boolean);
    if (!items.length && !contacto.contenido && !contacto.content) return null;
    return {
        title: contacto.title ?? contacto.titulo ?? "Contacto",
        content: contacto.content ?? contacto.contenido ?? "",
        items,
        note: "",
    };
};

// Normaliza el JSON legal a { sections: [...], lastUpdated }.
// Tolera la forma plana en inglés y la forma anidada en español
// (terminos_y_condiciones | politica_de_privacidad -> secciones, contacto, ultima_actualizacion).
const normalizeLegalData = (data) => {
    if (!data || typeof data !== "object") return { sections: [], lastUpdated: "" };

    // Si ya viene en la forma plana esperada, usarla; si no, buscar el contenedor anidado.
    let container = data;
    if (!Array.isArray(data.sections) && !Array.isArray(data.secciones)) {
        container =
            Object.values(data).find(
                (v) =>
                    v && typeof v === "object" &&
                    (Array.isArray(v.sections) || Array.isArray(v.secciones))
            ) || data;
    }

    const rawSections = container.sections || container.secciones || [];
    const sections = (Array.isArray(rawSections) ? rawSections : [])
        .map(normalizeSection)
        .filter(Boolean);

    // El bloque "contacto" es hermano de "secciones", no parte del array: lo agregamos al final.
    const contactSection = contactToSection(container.contacto || container.contact);
    if (contactSection) sections.push(contactSection);

    const lastUpdated =
        container.lastUpdated ?? container.ultima_actualizacion ?? "";

    return { sections, lastUpdated };
};

const LegalModal = ({ isOpen, onClose, title, contentType }) => {
    const [sections, setSections] = useState([]);
    const [lastUpdated, setLastUpdated] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!isOpen) return;

            setLoading(true);
            try {
                const file = contentType === "terms" ? "resources/terms.json" : "resources/privacypolicy.json";
                const { getFile } = await import("../../storageService");
                const data = await getFile(file);

                if (data) {
                    const { sections, lastUpdated } = normalizeLegalData(data);
                    setSections(sections);
                    setLastUpdated(lastUpdated);
                }
            } catch (error) {
                console.error(`Error loading ${contentType}:`, error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isOpen, contentType]);

    if (!isOpen) return null;

    return (
        <div className="legal-modal-backdrop">
            <div className="legal-modal">
                <button className="close-btn" onClick={onClose}>×</button>

                <div className="legal-container">
                    <h2 className="legal-title">{title}</h2>
                    <p className="legal-last-updated">
                        <strong>Última actualización:</strong> {lastUpdated}
                    </p>

                    {loading ? (
                        <p className="loading-text">Cargando...</p>
                    ) : (
                        (sections || []).map((section, index) => (
                            <div key={index} className="legal-section">
                                <h3>{section.title}</h3>
                                {section.content && <p>{section.content}</p>}
                                {section.items && section.items.length > 0 && (
                                    <ul className="legal-section-items">
                                        {section.items.map((item, i) => (
                                            <li key={i}>
                                                {item.key && <strong>{item.key}: </strong>}
                                                {item.value}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {section.note && (
                                    <p className="legal-section-note">{section.note}</p>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default LegalModal;